import { FunctionComponent, ReactElement } from 'react';
import { Stack, IconButton, Paper, Box, Chip, Tooltip } from '@mui/material';
import { EditRounded, DeleteRounded, LockResetRounded } from '@mui/icons-material';
import { CustomerUser, Role } from '@/core/entities';
import { ActionItem, Actions } from '@/components/ui/table/types';
import Backdrop from '@/components/ui/backdrop';
import DataGrid, {
	Column,
	FilterRow,
	HeaderFilter,
	Paging,
	SearchPanel,
	Sorting,
	Toolbar,
	Item,
} from 'devextreme-react/data-grid';

import CustomerUsersTableHeader, {
	CustomerUsersTableHeaderProps,
} from '../customer-users-table/CustomerUsersTableHeader';

interface CustomerUserTable {
	data: CustomerUser[];
	roles?: Role[];
	actions?: ActionItem<CustomerUser>[];
	isLoading?: boolean;
}

interface CustomerUserManagementBaseProps {
	table: CustomerUserTable;
	header?: CustomerUsersTableHeaderProps;
}

const CustomerUserManagementBase: FunctionComponent<CustomerUserManagementBaseProps> = ({
	header,
	table,
}): ReactElement => {
	const { data, roles, actions, isLoading } = table;

	const getRoleName = (roleId: string) => {
		const role = roles?.find((r) => r.id === roleId);
		return role?.title || '';
	};

	const editAction = actions?.find((a) => a.type === Actions.EDIT);
	const deleteAction = actions?.find((a) => a.type === Actions.DELETE);
	const sendTempPasswordAction = actions?.find((a) => a.type === Actions.SEND_TEMP_PASSWORD);

	const renderActionsCell = (cellData: { data: CustomerUser }) => {
		const user = cellData.data;
		const canEdit = editAction?.permitted ? editAction.permitted(user) : true;
		const canDelete = deleteAction?.permitted ? deleteAction.permitted(user) : true;
		const canSendTempPassword = sendTempPasswordAction?.permitted
			? sendTempPasswordAction.permitted(user)
			: true;

		return (
			<Stack direction="row" spacing={1}>
				{editAction && canEdit && (
					<IconButton
						size="small"
						color="primary"
						onClick={(e) => {
							e.stopPropagation();
							editAction.callback(user);
						}}
					>
						<EditRounded fontSize="small" />
					</IconButton>
				)}
				{deleteAction && canDelete && (
					<IconButton
						size="small"
						color="error"
						onClick={(e) => {
							e.stopPropagation();
							deleteAction.callback(user);
						}}
					>
						<DeleteRounded fontSize="small" />
					</IconButton>
				)}
				{sendTempPasswordAction && canSendTempPassword && (
					<Tooltip title="Send temporary password">
						<IconButton
							size="small"
							color="default"
							onClick={(e) => {
								e.stopPropagation();
								sendTempPasswordAction.callback(user);
							}}
						>
							<LockResetRounded fontSize="small" />
						</IconButton>
					</Tooltip>
				)}
			</Stack>
		);
	};

	const renderRoleCell = (cellData: { data: CustomerUser }) => {
		const roleName = getRoleName(cellData.data.roleId);
		return <Chip size="small" variant="outlined" color="primary" label={roleName} />;
	};

	return (
		<Stack direction="column" spacing={3}>
			{isLoading ? (
				<Backdrop />
			) : (
				<Box component={Paper} p={3}>
					<DataGrid
						dataSource={data}
						showBorders={true}
						columnAutoWidth={true}
						rowAlternationEnabled={true}
						allowColumnResizing={true}
						allowColumnReordering={true}
					>
						<Sorting mode="multiple" />
						<FilterRow visible={true} />
						<HeaderFilter visible={true} />
						<SearchPanel visible={true} highlightCaseSensitive={true} />
						<Paging defaultPageSize={50} enabled={true} />
						<Toolbar>
							<Item location="before">{header && <CustomerUsersTableHeader {...header} />}</Item>
							<Item name="searchPanel" location="after" />
						</Toolbar>

						<Column
							caption="User"
							calculateCellValue={(rowData: CustomerUser) =>
								`${rowData.firstName} ${rowData.lastName}`
							}
						/>
						<Column dataField="email" caption="Email address" />
						<Column
							dataField="roleId"
							caption="Role"
							cellRender={renderRoleCell}
							calculateCellValue={(rowData: CustomerUser) => getRoleName(rowData.roleId)}
						/>
						<Column dataField="pin" caption="PIN" alignment="right" />
						{actions && actions.length > 0 && (
							<Column
								caption="Actions"
								width={100}
								allowFiltering={false}
								allowSorting={false}
								allowReordering={false}
								cellRender={renderActionsCell}
							/>
						)}
					</DataGrid>
				</Box>
			)}
		</Stack>
	);
};

export default CustomerUserManagementBase;
